/**
 * Deep link para a página de chamados (query `redirect=chamado&wid=`).
 * Mesma regra do `components/layout/chamado_redirect.py`: o `wid` fica guardado
 * só enquanto a aba está aberta nesta sessão do painel (em memória, como o
 * `st.session_state`). Recarregar a página ou digitar outro endereço perde o
 * `wid`, e a página de chamado mostra o aviso de link inválido.
 *
 * O login não perde o `wid`: o servidor devolve o navegador para o mesmo
 * endereço (com a query) depois do Google.
 */
import { type RouterHistory } from '@tanstack/react-router'

import { CAMINHO_CHAMADO } from '@/lib/navegacao'

/** Estado da sessão da aba (some ao recarregar). */
const sessaoDaAba: { wid: string | null; pendente: boolean; abrindo: boolean } = {
  wid: null,
  pendente: false,
  abrindo: false,
}

/**
 * Diz se o painel está no meio do desvio para `/chamado` (link do comentário).
 * Nesse intervalo a casca do painel não desenha nada: no painel antigo, o
 * Início nunca aparecia antes do chamado.
 */
export function chamadoEmAbertura(): boolean {
  return sessaoDaAba.abrindo
}

/** A página de chamado já abriu: o painel volta a desenhar normalmente. */
export function concluirAberturaDoChamado(): void {
  sessaoDaAba.abrindo = false
}

function normalizarWid(bruto: string | null | undefined): string | null {
  if (bruto === null || bruto === undefined) return null
  const texto = String(bruto).trim()
  return texto ? texto : null
}

/**
 * Guarda o `wid` da query (`?redirect=chamado&wid=`), se houver.
 * Devolve true quando a query trouxe um `wid` válido.
 */
export function capturarRedirectDeChamado(search: string = window.location.search): boolean {
  const qp = new URLSearchParams(search)
  if (qp.get('redirect') !== 'chamado') return false
  const wid = normalizarWid(qp.get('wid'))
  if (wid === null) return false
  sessaoDaAba.wid = wid
  sessaoDaAba.pendente = true
  return true
}

/** Diz se a query pede para abrir o chamado (sem guardar nada). */
export function queryPedeChamado(search: string): boolean {
  const qp = new URLSearchParams(search)
  return qp.get('redirect') === 'chamado' && normalizarWid(qp.get('wid')) !== null
}

/** Token `wid` guardado para o fluxo de chamado, se válido. */
export function obterWidDoChamado(): string | null {
  return normalizarWid(sessaoDaAba.wid)
}

/** Resolve o `wid` da sessão ou da query string (`redirect=chamado&wid=`). */
export function resolverWidDoChamado(): string | null {
  const wid = obterWidDoChamado()
  if (wid !== null) return wid
  capturarRedirectDeChamado()
  return obterWidDoChamado()
}

/**
 * Após o login, informa (uma única vez) se é preciso abrir a página de chamado.
 * Devolve true quando o chamador deve navegar para `/chamado`.
 */
export function consumirRedirectDeChamado(): boolean {
  if (!sessaoDaAba.pendente) return false
  sessaoDaAba.pendente = false
  return obterWidDoChamado() !== null
}

/**
 * Desvio do link do comentário (`?redirect=chamado&wid=`), na mesma ordem do
 * painel antigo e sempre criando entradas novas no histórico (o botão Voltar
 * do navegador passa por elas):
 * 1. guarda o `wid` e tira a query do endereço (vale também para
 *    `/chamado?redirect=chamado&wid=`);
 * 2. abre `/chamado`.
 *
 * `hrefEsperado` é o endereço que a tela viu ao desenhar: se o endereço já
 * mudou (efeito rodando de novo), nada é feito — assim não duplica entradas.
 * Devolve true quando algum desvio aconteceu.
 */
export function aplicarDesviosDeEndereco(history: RouterHistory, hrefEsperado: string): boolean {
  const { pathname, search, href } = history.location
  if (href !== hrefEsperado) return false

  // O histórico do roteador junta vários `push` seguidos numa entrada só;
  // `flush` grava cada um na hora, para cada desvio virar uma entrada própria.
  const empilhar = (caminho: string) => {
    history.push(caminho)
    history.flush()
  }
  const abrirChamado = capturarRedirectDeChamado(search) && consumirRedirectDeChamado()
  if (!abrirChamado) return false
  if (pathname !== CAMINHO_CHAMADO) {
    sessaoDaAba.abrindo = true
    // Já na página de chamado, basta tirar a query (uma entrada só).
    empilhar(pathname)
  }
  empilhar(CAMINHO_CHAMADO)
  return true
}
